import clientTheme from "../gatsby-theme-material-ui-top-layout/theme"
import { brandPalette } from "../gatsby-theme-material-ui-top-layout/brandPalette"

/**
 * HELPER: Base structure to avoid repeating keys.
 * Default button structure used by ~90% of your palette.
 */
const createColorEntry = overrides => ({
  id: "color",
  contrastButton: { contained: "primary", outlined: "secondary" },
  hoverColor: "primary.main",
  ...overrides,
})

// ContrasBase Text is card title/text
//Contrast Base is card background
// Contrast Text is text on background color + filters
// FUTURE - contrastLink: "primary.main", // Default fallback for a href inside text compopnent
export const contrastBrandPalette = {
  // --- ORIGINAL BRAND SET ---
  "Original Primary": createColorEntry({
    value: brandPalette["Original Primary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Original Primary"]?.value,
    contrastSvg: brandPalette["White"]?.value,
  }),
  "Original Medium": createColorEntry({
    value: brandPalette["Original Medium"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["Original Large"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["Original Large"]?.value,
  }),
  "Original Large": createColorEntry({
    value: brandPalette["Original Large"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Original Large"]?.value,
    contrastSvg: brandPalette["Rich Yolk Primary"]?.value,
  }),

  // --- NAVY GROUP ---
  Navy: createColorEntry({
    value: brandPalette["Navy"]?.value,
    textColour: "white.main",
    tonalLight: {
      mui: "secondary.mid",
      hex: clientTheme.palette.secondary.mid,
    },
    svg: {
      default: { mui: "primary", hex: clientTheme.palette.primary.main },
      active: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    },
    line: { mui: "secondary.main", hex: clientTheme.palette.secondary.main },
    pie: {
      default: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
      active: {
        mui: "secondary.main",
        hex: clientTheme.palette.secondary.main,
      },
    },
    spiro: { fill: clientTheme.palette.white.main, opacity: 0.4 },
    divider: { mui: "white.main", hex: clientTheme.palette.white.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),
  "Navy Uncoated": createColorEntry({
    value: brandPalette["Navy Uncoated"]?.value,
    textColour: "white.main",
    tonalLight: {
      mui: "secondary.light",
      hex: clientTheme.palette.secondary.light,
    },
    svg: {
      default: { mui: "primary", hex: clientTheme.palette.primary.main },
      active: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    },
    line: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
    pie: {
      default: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
      active: {
        mui: "secondary.light",
        hex: clientTheme.palette.secondary.light,
      },
    },
    spiro: { fill: clientTheme.palette.white.main, opacity: 0.4 },
    divider: { mui: "white.main", hex: clientTheme.palette.white.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),
  "Mid Navy": createColorEntry({
    value: brandPalette["Mid Navy"]?.value,
    textColour: "white.main",
    tonalLight: {
      mui: "secondary.mid",
      hex: clientTheme.palette.secondary.mid,
    },
    svg: {
      default: { mui: "primary", hex: clientTheme.palette.primary.main },
      active: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    },
    line: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
    pie: {
      default: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
      active: {
        mui: "secondary.main",
        hex: clientTheme.palette.secondary.main,
      },
    },
    spiro: { fill: clientTheme.palette.white.main, opacity: 0.4 },
    divider: { mui: "white.main", hex: clientTheme.palette.white.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),

  // --- CORAL GROUP ---
  Coral: createColorEntry({
    value: brandPalette["Coral"]?.value,
    textColour: "white.main",
    tonalLight: {
      mui: "primary.light",
      hex: clientTheme.palette.primary.light,
    },
    svg: {
      default: {
        mui: "primary.lightest",
        hex: clientTheme.palette.primary.lightest,
      },
      active: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    },
    line: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    pie: {
      default: { mui: "secondary.mid", hex: clientTheme.palette.secondary.mid },
      active: {
        mui: "secondary.main",
        hex: clientTheme.palette.secondary.main,
      },
    },
    spiro: { fill: clientTheme.palette.primary.lightest, opacity: 1 },
    divider: { mui: "white.main", hex: clientTheme.palette.white.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),
  "Coral Light": createColorEntry({
    value: brandPalette["Coral Light"]?.value,
    textColour: "white.main",
    tonalLight: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    svg: {
      default: { mui: "secondary", hex: clientTheme.palette.secondary.main },
      active: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    },
    line: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    pie: {
      default: { mui: "primary.light", hex: clientTheme.palette.primary.light },
      active: {
        mui: "primary.lightest",
        hex: clientTheme.palette.primary.lightest,
      },
    },
    spiro: { fill: clientTheme.palette.white.main, opacity: 1 },
    divider: { mui: "white.main", hex: clientTheme.palette.white.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),

  // --- GREY GROUP ---
  "Light Grey": createColorEntry({
    value: brandPalette["Light Grey"]?.value,
    textColour: "secondary.main",
    tonalLight: { mui: "white.main", hex: clientTheme.palette.white.main },
    svg: {
      default: { mui: "primary", hex: clientTheme.palette.primary.main },
      active: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    },
    line: { mui: "text.mid", hex: clientTheme.palette.text.mid },
    pie: {
      default: { mui: "white.main", hex: clientTheme.palette.white.main },
      active: { mui: "grey.main", hex: clientTheme.palette.grey.main },
    },
    spiro: { fill: clientTheme.palette.secondary.main, opacity: 0.4 },
    divider: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    contrastButton: { contained: "primary", outlined: "white" },
  }),

  // --- YOLK & OPULENT ---
  "Rich Yolk Primary": createColorEntry({
    value: brandPalette["Rich Yolk Primary"]?.value,
    contrastButton: { contained: "secondary", outlined: "secondary" },
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["Rich Yolk Secondary"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: "#2661ab", // Custom hover for this colour, falls back to Orange
    contrastLink: brandPalette["White"]?.value,
  }),
  "Rich Yolk Secondary": createColorEntry({
    value: brandPalette["Rich Yolk Secondary"]?.value,
    contrastButton: { contained: "secondary", outlined: "secondary" }, // Or "tertiary" label if used as logic
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Rich Yolk Opulent Hot Foil": createColorEntry({
    value: brandPalette["Rich Yolk Opulent Hot Foil"]?.value,
    contrastButton: { contained: "primary", outlined: "secondary" },
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastText: brandPalette["Opulent Blue"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Rich Yolk Opulent Primary": createColorEntry({
    value: brandPalette["Rich Yolk Opulent Primary"]?.value,
    contrastButton: { contained: "secondary", outlined: "secondary" },
    contrastBase: brandPalette["Rich Yolk Opulent Hot Foil"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["Rich Yolk Opulent Hot Foil"]?.value,
    contrastText: brandPalette["Rich Yolk Opulent Hot Foil"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Rich Yolk Opulent Secondary": createColorEntry({
    value: brandPalette["Rich Yolk Opulent Secondary"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),

  // --- SUPER EGGS ---
  "Super Eggs Primary": createColorEntry({
    value: brandPalette["Super Eggs Primary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["Super Eggs Secondary"]?.value,
    contrastBaseText: brandPalette["Super Eggs Primary"]?.value,
  }),
  "Super Eggs Secondary": createColorEntry({
    value: brandPalette["Super Eggs Secondary"]?.value,
    contrastText: brandPalette["Original Large"]?.value,
    contrastBase: brandPalette["Original Large"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["Original Large"]?.value,
  }),
  "Super Eggs Secondary Dark": createColorEntry({
    value: brandPalette["Super Eggs Secondary Dark"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["White"]?.value,
  }),
  "Super Eggs Secondary Accent": createColorEntry({
    value: brandPalette["Super Eggs Secondary Accent"]?.value,
    contrastButton: { contained: "primary", outlined: "secondary" },
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),

  // --- DUCKS & QUAILS ---
  "Dabbling Duck Primary": createColorEntry({
    value: brandPalette["Dabbling Duck Primary"]?.value,
    contrastSvg: brandPalette["Dabbling Duck Secondary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Dabbling Duck Primary"]?.value,
    buttonContrast: { contained: "primary", outlined: "primary" },
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Dabbling Duck Secondary": createColorEntry({
    value: brandPalette["Dabbling Duck Secondary"]?.value,
    contrastSvg: brandPalette["Dabbling Duck Primary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Dabbling Duck Secondary"]?.value,
    buttonContrast: { contained: "primary", outlined: "primary" },
  }),
  Duck: createColorEntry({
    value: brandPalette["Duck"]?.value,
    contrastSvg: brandPalette["Dabbling Duck Secondary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Duck"]?.value,
    buttonContrast: { contained: "primary", outlined: "primary" },
  }),
  "Duck Egg": createColorEntry({
    value: brandPalette["Duck Egg"]?.value,
    contrastSvg: brandPalette["Dabbling Duck Primary"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    buttonContrast: { contained: "primary", outlined: "primary" },
  }),
  "Quirky Quail Primary": createColorEntry({
    value: brandPalette["Quirky Quail Primary"]?.value,
    contrastSvg: brandPalette["Rich Yolk Primary"]?.value,
    contrastText: brandPalette["Rich Yolk Primary"]?.value,
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    buttonContrast: { contained: "primary", outlined: "primary" },
  }),
  "Quirky Quail Secondary": createColorEntry({
    value: brandPalette["Quirky Quail Secondary"]?.value,
    contrastSvg: brandPalette["Original Large"]?.value,
    contrastText: brandPalette["Original Large"]?.value,
    contrastBase: brandPalette["Original Large"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
  }),

  // --- MISC VARIATIONS ---
  "Grand Primary": createColorEntry({
    value: brandPalette["Grand Primary"]?.value,
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastText: brandPalette["Opulent Blue"]?.value,
  }),
  "Opulent Blue": createColorEntry({
    value: brandPalette["Opulent Blue"]?.value,
    contrastText: brandPalette["Rich Yolk Primary"]?.value,
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
  }),
  "Grand Yolk": createColorEntry({
    value: brandPalette["Grand Yolk"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Grand Yolk"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  Grand: createColorEntry({
    value: brandPalette["Grand"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  Yolk: createColorEntry({
    value: brandPalette["Yolk"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Yolk"]?.value,
    contrastSvg: brandPalette["Yolk Yellow"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Yolk Yellow": createColorEntry({
    value: brandPalette["Yolk Yellow"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  Whisked: createColorEntry({
    value: brandPalette["Whisked"]?.value,
    contrastButton: { contained: "primary", outlined: "primary" },
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
  }),
  Albumen: createColorEntry({
    value: brandPalette["Albumen"]?.value,
    contrastText: brandPalette["Original Large"]?.value,
    contrastBase: brandPalette["Original Large"]?.value,
    contrastBaseText: brandPalette["White"]?.value,
    contrastSvg: brandPalette["Original Large"]?.value,
  }),
  Whole: createColorEntry({
    value: brandPalette["Whole"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
  }),
  "Whole White": createColorEntry({
    value: brandPalette["Whole White"]?.value,
    contrastText: brandPalette["Grand"]?.value,
    contrastBase: brandPalette["Quirky Quail Secondary"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["Grand"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),
  "Whole Yolk": createColorEntry({
    value: brandPalette["Whole Yolk"]?.value,
    contrastText: brandPalette["White"]?.value,
    contrastBase: brandPalette["White"]?.value,
    contrastBaseText: brandPalette["Opulent Blue"]?.value,
    contrastSvg: brandPalette["White"]?.value,
    hoverColor: brandPalette["Original Primary"]?.value, // Custom hover for this colour, falls back to Orange
  }),

  // --- WHITE (The standard fallback) ---
  White: createColorEntry({
    value: brandPalette["White"]?.value,
    textColour: "secondary.main",
    contrastText: "secondary.main",
    tonalLight: {
      mui: "primary.lightest",
      hex: clientTheme.palette.primary.lightest,
    },
    svg: {
      default: { mui: "primary", hex: clientTheme.palette.primary.main },
      active: { mui: "white.main", hex: clientTheme.palette.white.main },
    },
    line: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    pie: {
      default: {
        mui: "primary.lightest",
        hex: clientTheme.palette.primary.lightest,
      },
      active: { mui: "primary.main", hex: clientTheme.palette.primary.main },
    },
    spiro: { fill: clientTheme.palette.secondary.main, opacity: 0.4 },
    divider: { mui: "secondary.main", hex: clientTheme.palette.secondary.main },
    contrastButton: { contained: "primary", outlined: "white" },
    contrastBase: brandPalette["Original Medium"]?.value,
    contrastBaseText: "primary.main",
    contrastSvg: brandPalette["Original Medium"]?.value,
  }),
}

/**
 * UTILS
 */
export const convertHexToRGBA = (hexCode, opacity = 1) => {
  if (!hexCode) return null
  let hex = hexCode.replace("#", "")
  if (hex.length === 3) hex = [...hex].map(x => x + x).join("")
  const [r, g, b] = hex.match(/.{2}/g).map(x => parseInt(x, 16))
  const finalOpacity = opacity > 1 && opacity <= 100 ? opacity / 100 : opacity
  return `rgba(${r},${g},${b},${finalOpacity})`
}

export const getContrastColour = backgroundColour => {
  const label = backgroundColour?.label
  // Specific lookup -> Fallback to White configuration
  return contrastBrandPalette[label] || contrastBrandPalette["White"]
}
